import { useState } from 'react';

import { BottomSheet } from '@/components/BottomSheet';
import { usePlatform } from '@/platform/usePlatform';
import { forgetSession } from '@/services/auth';

import { describeOutcome, PLATFORM_NAMES } from './format';
import { errorText, useConfirmLink, usePreviewLink } from './useAccountLink';

/** ВКонтакте/Одноклассники: ввести код из Telegram, увидеть исход, подтвердить. */
export function RedeemCodePanel() {
  const platform = usePlatform();
  const [value, setValue] = useState('');
  const preview = usePreviewLink();
  const confirm = useConfirmLink();
  const digits = value.replace(/\D/g, '');
  const complete = digits.length === 6;

  if (confirm.data) {
    const survivor = PLATFORM_NAMES[confirm.data.survivorPlatform];
    const absorbed = PLATFORM_NAMES[confirm.data.absorbedPlatform];

    return (
      <div role="status" className="flex flex-col gap-3 pt-4">
        <p className="text-sm text-text">
          Аккаунт {absorbed} присоединён к аккаунту {survivor}. Баланс и история — из {survivor}.
        </p>
        <button
          type="button"
          onClick={() => {
            // Сессия могла принадлежать поглощённому аккаунту: забываем её и
            // перезапускаем — AuthProvider получит сессию главного аккаунта.
            void forgetSession()
              .catch(() => undefined)
              .then(() => platform.restart('/settings'));
          }}
          className="btn btn-primary self-start px-4 py-3 text-sm"
        >
          Продолжить
        </button>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-3 pt-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (complete) {
          preview.mutate(digits);
        }
      }}
    >
      <label htmlFor="account-link-code" className="text-sm text-text-2">
        Код из Студии в Telegram (Настройки → «Связанные площадки»)
      </label>
      <p className="text-xs text-text-3">Вводите только код из своего Telegram.</p>
      <input
        id="account-link-code"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={value}
        onChange={(event) => {
          setValue(event.target.value.replace(/\D/g, '').slice(0, 6));
          preview.reset();
        }}
        className="rounded-xl border border-border bg-surface px-4 py-3 text-lg tabular-nums tracking-widest text-text"
      />
      <button
        type="submit"
        disabled={!complete || preview.isPending}
        className="btn btn-primary self-start px-4 py-3 text-sm"
      >
        {preview.isPending ? 'Проверяем…' : 'Проверить код'}
      </button>

      {preview.isError && (
        <p role="alert" className="text-sm text-danger">
          {errorText(preview.error)}
        </p>
      )}

      {preview.data && (
        <BottomSheet
          title="Привязать аккаунты?"
          hint="Отменить привязку потом нельзя."
          closeLabel="Отмена"
          onClose={() => preview.reset()}
        >
          <div className="flex flex-col gap-3">
            <p className="text-sm text-text">{describeOutcome(preview.data)}</p>
            <p className="text-xs text-text-3">
              Код должен быть из вашего Telegram. Если его прислал кто-то другой, не подтверждайте: так он получит доступ к вашему аккаунту.
            </p>

            {confirm.isError && (
              <p role="alert" className="text-sm text-danger">
                {errorText(confirm.error)}
              </p>
            )}

            <button
              type="button"
              onClick={() => confirm.mutate(digits)}
              disabled={confirm.isPending}
              className="btn btn-primary w-full px-4 py-3"
            >
              {confirm.isPending ? 'Привязываем…' : 'Привязать'}
            </button>
          </div>
        </BottomSheet>
      )}
    </form>
  );
}
